// Renders the burner illustration. Background image + any overlay images
// whose parsed_show_rule matches current state. URLs in the CPQ export are
// relative NetSuite paths (/core/media/media.nl?id=...&c=<account>&h=<hash>)
// — they're HMAC-signed for public access, so prepending the account's
// NetSuite host should load them. If the load fails (network, expired hash,
// or local dev without internet), we fall back to a labeled placeholder.

import { useMemo, useState } from "react";
import type { CompositeImage, ProductMeta, Resources } from "../types";
import { useAnswers } from "../state/AnswersContext";
import { resourceUrl } from "../data/loader";
import { evalRule } from "../util/rule";

type Props = {
  compositeImages: CompositeImage[];
  product: ProductMeta;
  resources: Resources;
  resourceFiles: Record<string, string>;
};

function netsuiteUrl(account: string, relative: string): string {
  if (!relative) return "";
  if (/^https?:\/\//i.test(relative)) return relative;
  const path = relative.startsWith("/") ? relative : `/${relative}`;
  return `https://${account}.app.netsuite.com${path}`;
}

export default function CompositeImagePanel({
  compositeImages,
  product,
  resources,
  resourceFiles,
}: Props) {
  const { answers } = useAnswers();

  const account = product.accountInfo.accountName;

  // Prefer the local copy of a resource image (self-contained, no expiring
  // NetSuite media hash); fall back to the NetSuite URL when there isn't one.
  // The path comes from resourceUrl(), NOT built here: images live under the ACTIVE
  // product's folder (/data/<slug>/resources/), and this used to hardcode a single
  // product's path — which would have served Natural Draft artwork inside every
  // other configurator.
  const localSrc = (resId: unknown): string => {
    const f = resId != null ? resourceFiles[String(resId)] : undefined;
    return f ? resourceUrl(f) : "";
  };
  const cpiHeight =
    Number(product.fields.custrecord_cpqc_product_cpi_height) || 300;

  const active = useMemo(() => {
    return [...compositeImages]
      .filter((c) =>
        evalRule(c.custrecord_cpqc_cpi_parsed_show_rule ?? "", answers)
      )
      .sort(
        (a, b) =>
          Number(a.custrecord_cpqc_cpi_sequence_number || 0) -
          Number(b.custrecord_cpqc_cpi_sequence_number || 0)
      );
  }, [compositeImages, answers]);

  return (
    <div className="cpq-composite">
      <header>Product Illustration</header>
      <div
        className="cpq-composite-stack"
        style={{ minHeight: cpiHeight, height: cpiHeight }}
      >
        {/* The product default image is a real photograph, not a schematic, so
            it is intentionally NOT rendered as a background — the matching
            composite overlays carry the line drawing. */}
        {active.map((c) => {
          const url =
            localSrc(c.custrecord_cpqc_cpi_image) ||
            netsuiteUrl(account, c.custrecord_cpqc_cpi_img_url);
          const resName =
            (resources[c.custrecord_cpqc_cpi_image]?.name as string) ?? c.name;
          return (
            <ImageLayer
              key={c.id}
              src={url}
              alt={resName}
              label={c.name}
              top={c.custrecord_cpqc_cpi_offset_top}
              left={c.custrecord_cpqc_cpi_offset_left}
            />
          );
        })}
      </div>
      {active.length === 0 && (
        <div className="cpq-composite-empty">
          No overlays match the current configuration.
        </div>
      )}
    </div>
  );
}

function ImageLayer({
  src,
  alt,
  label,
  isBackground,
  top,
  left,
}: {
  src: string;
  alt: string;
  label: string;
  isBackground?: boolean;
  top?: string;
  left?: string;
}) {
  const [failed, setFailed] = useState(false);

  if (failed || !src) {
    // The product's default background image isn't publicly signed (its URL has
    // no NetSuite media hash), so it can't load. Rather than show a stray
    // "Background" box over the illustration, fall back to the panel's gradient
    // and let the matching overlays carry the drawing.
    if (isBackground) return null;
    return (
      <div
        className="cpq-image-placeholder cpq-image-overlay"
        style={{
          top: top ? Number(top) : undefined,
          left: left ? Number(left) : undefined,
        }}
      >
        <div className="cpq-image-label">{label}</div>
        <div className="cpq-image-name">{alt}</div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      title={label}
      className={`cpq-image-layer ${
        isBackground ? "is-background" : "is-overlay"
      }`}
      style={{
        top: top ? Number(top) : undefined,
        left: left ? Number(left) : undefined,
      }}
      onError={() => setFailed(true)}
    />
  );
}
